import React, { useEffect, useState } from "react";
import { Button, Drawer, Table, Tag } from "antd";
import Title from "antd/lib/typography/Title";
import { getFormattedAmount } from "Shared/Utils";
import getCurrencySymbol from "Shared/CurrancySymbol";
import { PerformanceTag } from "Shared/PerformanceTag";
import { Tokens } from "Shared/Tokens";
import {
    CloseOutlined,
    DownOutlined,
    ReloadOutlined,
    UpOutlined,
} from "@ant-design/icons";

const resolveRowMetrics = (record = {}) => {
    const txList = Array.isArray(record.transactions)
        ? record.transactions
        : [];
    const latest = record.portfolio?.latest || {};
    const previous = record.portfolio?.previous || {};
    let numCoins = null;
    if (record.numberOfCoins !== undefined) {
        numCoins = Number(record.numberOfCoins) || 0;
    } else if (latest.numberOfCoins !== undefined) {
        numCoins = Number(latest.numberOfCoins) || 0;
    }

    const isExited =
        Boolean(record.isExited) ||
        (numCoins === 0 &&
            (txList.length > 0 ||
                Number(previous.numberOfCoins) > 0 ||
                Boolean(latest.sellInfo)));

    let txBought = 0;
    let txProceeds = 0;
    let txBasis = 0;
    txList.forEach((tx) => {
        const snap = tx?.portfolioSnapshot || {};
        const prevInv = Number(snap?.previous?.investment) || 0;
        const latestInv = Number(snap?.latest?.investment) || 0;
        const prevCoins = Number(snap?.previous?.numberOfCoins) || 0;
        const latestCoins = Number(snap?.latest?.numberOfCoins) || 0;
        const txPrice = Number(snap?.transactionMarketPrice) || 0;

        if (tx?.type === "buy" || latestInv > prevInv) {
            txBought += Math.max(0, latestInv - prevInv);
        } else if (tx?.type === "sell" || prevCoins > latestCoins) {
            txProceeds +=
                Number(snap?.latest?.sellInfo?.amountToSell) ||
                Math.max(0, (prevCoins - latestCoins) * txPrice);
            txBasis +=
                Number(snap?.latest?.sellInfo?.intialCapitalForPurchase) ||
                Math.max(0, prevInv - latestInv);
        }
    });

    if (isExited) {
        const lifetimeBought =
            Number(record.lifetimeBought) ||
            txBought ||
            Number(previous.investment) ||
            Number(latest.sellInfo?.intialCapitalForPurchase) ||
            Number(record.amount) ||
            0;
        const realizedProceeds =
            Number(record.realizedProceeds) ||
            txProceeds ||
            Number(latest.sellInfo?.amountToSell) ||
            Number(latest.returns) ||
            0;
        const costBasis = txBasis || lifetimeBought;
        const realizedPl =
            record.realizedPl !== undefined
                ? Number(record.realizedPl) || 0
                : realizedProceeds - costBasis;
        let realizedPlPct = 0;
        if (record.realizedPlPct !== undefined) {
            realizedPlPct = Number(record.realizedPlPct) || 0;
        } else if (costBasis > 0) {
            realizedPlPct = (realizedPl / costBasis) * 100;
        }

        return {
            isExited: true,
            investment: lifetimeBought,
            currentValue: realizedProceeds,
            plValue: realizedPl,
            plPct: realizedPlPct,
        };
    }

    let investment = Number(latest.investment) || 0;
    if (record.investment !== undefined) {
        investment = Number(record.investment) || 0;
    } else if (record.amount !== undefined) {
        investment = Number(record.amount) || 0;
    }

    const computedFromLatest =
        (Number(record.currentPrice) || Number(latest.marketPrice) || 0) *
        (numCoins || 0);
    const currentValue =
        record.currentValue !== undefined
            ? Number(record.currentValue) || 0
            : computedFromLatest || investment;
    const plValue =
        record.plValue !== undefined
            ? Number(record.plValue) || 0
            : currentValue - investment;
    let plPct = 0;
    if (record.plPct !== undefined) {
        plPct = Number(record.plPct) || 0;
    } else if (record.pl !== undefined) {
        plPct = Number(record.pl) || 0;
    } else if (investment > 0) {
        plPct = (plValue / investment) * 100;
    }

    return {
        isExited: false,
        investment,
        currentValue,
        plValue,
        plPct,
    };
};

const InvestmentList = (props) => {
    const {
        investments = [],
        headerStats = {},
        onClose = () => {},
        visible = false,
        refreshing = false,
        getAllPortfolioAssetsInfo = () => {},
        currency = "INR",
    } = props;

    const currSymbol = getCurrencySymbol(currency);
    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth < 768 : false
    );

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const columns = [
        {
            title: "Coin",
            dataIndex: "name",
            key: "name",
            align: "left",
            render: (name, record) => {
                const metrics = resolveRowMetrics(record);
                return (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            flexWrap: "wrap",
                        }}
                    >
                        <span
                            style={{
                                color: Tokens.textPrimary,
                                fontWeight: 500,
                                whiteSpace: "nowrap",
                            }}
                        >
                            {name}
                        </span>
                        {metrics.isExited && (
                            <Tag
                                style={{
                                    margin: 0,
                                    backgroundColor: Tokens.raised,
                                    color: Tokens.textSecondary,
                                    border: `1px solid ${Tokens.hairlineStrong}`,
                                    borderRadius: Tokens.radiusTag,
                                    fontSize: "11px",
                                    lineHeight: "16px",
                                    padding: "0 6px",
                                }}
                            >
                                Exited
                            </Tag>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Investment",
            dataIndex: "amountView",
            key: "amountView",
            align: "right",
            render: (_, record) => {
                const metrics = resolveRowMetrics(record);
                return (
                    <div
                        style={{
                            fontFamily: Tokens.fontMono,
                            fontVariantNumeric: "tabular-nums",
                            color: Tokens.textPrimary,
                            whiteSpace: "nowrap",
                        }}
                    >
                        <div>
                            {currSymbol}
                            {getFormattedAmount(metrics.investment, true, 2)}
                        </div>
                        {metrics.isExited && (
                            <div
                                style={{
                                    fontSize: "11px",
                                    color: Tokens.textMuted,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Lifetime bought
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Value",
            dataIndex: "currentValue",
            key: "currentValue",
            align: "right",
            render: (_, record) => {
                const metrics = resolveRowMetrics(record);
                return (
                    <div
                        style={{
                            fontFamily: Tokens.fontMono,
                            fontVariantNumeric: "tabular-nums",
                            color: Tokens.textPrimary,
                            whiteSpace: "nowrap",
                        }}
                    >
                        <div>
                            {currSymbol}
                            {getFormattedAmount(metrics.currentValue, true, 2)}
                        </div>
                        {metrics.isExited && (
                            <div
                                style={{
                                    fontSize: "11px",
                                    color: Tokens.textMuted,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Realized
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            title: "P&L",
            dataIndex: "pl",
            key: "pl",
            align: "right",
            render: (_, record) => {
                const metrics = resolveRowMetrics(record);
                let plColor = Tokens.textSecondary;
                if (metrics.plValue > 0) {
                    plColor = Tokens.positive;
                } else if (metrics.plValue < 0) {
                    plColor = Tokens.negative;
                }
                return (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-end",
                            gap: "2px",
                            fontFamily: Tokens.fontMono,
                            fontVariantNumeric: "tabular-nums",
                            whiteSpace: "nowrap",
                        }}
                    >
                        <span style={{ color: plColor, fontWeight: 500 }}>
                            {metrics.plValue >= 0 ? "+" : "-"}
                            {currSymbol}
                            {getFormattedAmount(
                                Math.abs(metrics.plValue),
                                true,
                                2
                            )}
                        </span>
                        <div>{PerformanceTag(metrics.plPct, true)}</div>
                    </div>
                );
            },
        },
    ];

    useEffect(() => {}, [headerStats.pl]);

    return (
        <div>
            <Drawer
                title={
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "12px",
                            maxWidth: Tokens.containerMaxWidth,
                            margin: "0 auto",
                            width: "100%",
                        }}
                    >
                        <Title
                            level={4}
                            style={{
                                margin: 0,
                                color: Tokens.textPrimary,
                                fontWeight: 500,
                                fontSize: "16px",
                                whiteSpace: "nowrap",
                                flexShrink: 0,
                            }}
                        >
                            Investment overview
                        </Title>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "flex-end",
                                flexDirection: "column",
                                gap: "2px",
                                minWidth: 0,
                            }}
                        >
                            <div
                                style={{
                                    fontSize: "11px",
                                    lineHeight: "1.2",
                                    color: Tokens.textSecondary,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Current value
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <span
                                    style={{
                                        color: Tokens.textPrimary,
                                        fontWeight: 600,
                                        fontSize: "15px",
                                        fontFamily: Tokens.fontMono,
                                        fontVariantNumeric: "tabular-nums",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {currSymbol}&nbsp;
                                    {getFormattedAmount(
                                        headerStats.currentValue
                                    )}
                                </span>
                                {PerformanceTag(headerStats.pl, true)}
                            </div>
                        </div>
                    </div>
                }
                placement={isMobile ? "bottom" : "right"}
                closable
                closeIcon={
                    <CloseOutlined style={{ color: Tokens.textSecondary }} />
                }
                width={isMobile ? "100%" : 640}
                height={isMobile ? "85%" : "100%"}
                push={false}
                onClose={onClose}
                open={visible}
                maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.75)" }}
                headerStyle={{
                    backgroundColor: Tokens.bg,
                    borderBottom: `1px solid ${Tokens.hairline}`,
                    padding: "14px 16px",
                }}
                bodyStyle={{
                    backgroundColor: Tokens.bg,
                    padding: "16px",
                }}
                footerStyle={{
                    backgroundColor: Tokens.card,
                    borderTop: `1px solid ${Tokens.hairline}`,
                    padding:
                        "10px 16px calc(10px + env(safe-area-inset-bottom, 0px))",
                    textAlign: "left",
                }}
                footer={
                    <div
                        style={{
                            maxWidth: Tokens.containerMaxWidth,
                            margin: "0 auto",
                            width: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "12px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "2px",
                                minWidth: 0,
                            }}
                        >
                            <div
                                style={{
                                    color: Tokens.textMuted,
                                    fontSize: "11px",
                                    lineHeight: "1.2",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Total investment
                            </div>
                            <div
                                style={{
                                    color: Tokens.textPrimary,
                                    fontWeight: 600,
                                    fontSize: "15px",
                                    lineHeight: "1.3",
                                    fontFamily: Tokens.fontMono,
                                    fontVariantNumeric: "tabular-nums",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {currSymbol}&nbsp;
                                {getFormattedAmount(
                                    headerStats.totalInvestment
                                )}
                            </div>
                        </div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                flexShrink: 0,
                            }}
                        >
                            <Button
                                type="default"
                                title="Refresh prices"
                                style={{
                                    height: "40px",
                                    minWidth: "40px",
                                    borderRadius: Tokens.radiusControl,
                                }}
                                onClick={() => getAllPortfolioAssetsInfo()}
                            >
                                <ReloadOutlined spin={refreshing} />
                            </Button>
                            <Button
                                title="Close overview"
                                style={{
                                    height: "40px",
                                    minWidth: "40px",
                                    borderRadius: Tokens.radiusControl,
                                }}
                                onClick={onClose}
                            >
                                {visible ? <DownOutlined /> : <UpOutlined />}
                            </Button>
                        </div>
                    </div>
                }
            >
                <div
                    style={{
                        maxWidth: Tokens.containerMaxWidth,
                        margin: "0 auto",
                        width: "100%",
                    }}
                >
                    {investments.map((investment) => {
                        return (
                            <div
                                style={{ padding: "12px 0" }}
                                key={investment.uid}
                            >
                                <Title
                                    level={5}
                                    style={{
                                        color: Tokens.textPrimary,
                                        fontWeight: 500,
                                        marginBottom: "8px",
                                    }}
                                >
                                    {investment.portfolio}
                                </Title>
                                <Table
                                    columns={columns}
                                    dataSource={investment.assets}
                                    size="small"
                                    bordered
                                    pagination={false}
                                    scroll={{ x: "max-content" }}
                                />
                            </div>
                        );
                    })}
                </div>
            </Drawer>
        </div>
    );
};

export default InvestmentList;
